import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomContainer } from "../../../../components/Containers/CustomContainer";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import { geSupplierProductReviews } from "../../../../services/supplier/productApiFn";
import useDebounce from "../../../../hooks/useDebounce";
import { handleOpenSnackBar } from "../../../../utils";
import {
  setReviewCurrentPage,
  setReviewPageLimit,
  setReviewTotalCount,
  setReviewTotalPages,
} from "../../../../lib/slices/paginationSlice";

const headerData = ["date", "productDetails", "userDetails"];

const tableRows = [
  "created_date",
  "reviewed_product_detail",
  "user_details_review",
];

export const Reviews = () => {
  // Dispatch Hook
  const dispatch = useDispatch();
  // All Reviews State
  const [allReviews, setAllReviews] = useState([]);
  // Loading State
  const [isLoading, setIsLoading] = useState(false);
  // User Data
  const userData = useSelector((state) => state.userData.user);
  // Search Keys
  const searchKeys = useSelector(
    (state) => state.productSearchPageReducer.productSearchPage,
  );
  // Get Pagination from Redux
  const pagination = useSelector((state) => state.paginationReducer);
  // Debounced Search Term
  const debouncedSearchTerm = useDebounce(searchKeys.reviewSearchVal, 1000);

  const fetchAllReviews = async (name, page, limit) => {
    let query = {
      search: name || "",
      page: page || 1,
      limit: limit || 20,
      supplierId: userData?._id,
    };
    try {
      setIsLoading(true);
      const response = await geSupplierProductReviews(query);
      if (response?.data.status === "success") {
        let reviews = response?.data?.data?.data;
        let pagination = response?.data?.data?.pagination;
        console.log(pagination, "paginationpaginationpagination");
        setAllReviews(reviews);
        dispatch(setReviewTotalCount(pagination?.total));
        dispatch(setReviewTotalPages(pagination?.totalPages));
        dispatch(setReviewCurrentPage(pagination?.page));
        dispatch(setReviewPageLimit(pagination?.limit));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchAllReviews(
        debouncedSearchTerm,
        pagination.reviewPage,
        pagination.reviewLimit,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    pagination.reviewPage,
    pagination.reviewLimit,
  ]);

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setReviewCurrentPage(page));
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setReviewPageLimit(limit));
    dispatch(setReviewCurrentPage(1));
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allReviews}
        isLoading={isLoading}
        showDynamicPagination={true}
        paginatedData={{
          total: pagination.reviewTotal,
          totalPages: pagination.reviewTotalPages,
          page: pagination.reviewPage,
          limit: pagination.reviewLimit,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    );
  }, [allReviews, isLoading]);

  return (
    <CustomContainer
      primaryHeading={t("allReviews")}
      secondaryHeading={t("reviewsDescription")}
    >
      {renderTable}
    </CustomContainer>
  );
};
